import { useCallback, useMemo } from "react";
import { ActivitySeries } from "@common/schemas/domains/activity/activity-timeline.schema";
import type { OrganizationHistoryFilter } from "@common/types/auditLog";
import { useOrganizationHistoryDailyCountsQueries } from "@api/organizationAuditLog.api";
import { ActivityPageTimeline } from "@components/activityTimeline/ActivityPageTimeline";
import type { ActivityTimelineRange } from "@components/activityTimeline/activityTimelineRange.lib";
import type { TimelineSeriesKey } from "@components/activityTimeline/activityTimelineSeries.lib";
import { toSliceActorPicks } from "@components/activityTimeline/activityTimelinePeople.lib";
import { ACTIVITY_TIMELINE_SERIES } from "@components/activityTimeline/activityTimelineSeries.lib";
import type { ActivityChartDensityControl } from "@components/activityTimeline/useActivityChartDensity";
import { useActivityTimelineCountsParams } from "@components/activityTimeline/useActivityTimelineCountsParams";
import type { DensityChartWindow } from "@design-system/DensityChart/DensityChart.lib";
import { CLEARED_ORGANIZATION_ACTOR_SELECTION } from "../components/OrganizationActorFilterCombobox";
import type { OrganizationActivityFilters } from "../organizationActivityFilters.types";
import { describeOrganizationHistoryStart } from "../organizationHistoryStart.lib";
import { useOrganizationActivityPeopleById } from "../OrganizationActivityPeopleProvider";

// The org list reads org membership and client task changes, which no rule or integration makes.
const ORGANIZATION_TIMELINE_SERIES = ACTIVITY_TIMELINE_SERIES.filter(
    ({ series }) =>
        series === ActivitySeries.PEOPLE || series === ActivitySeries.KICK,
);

interface OrganizationActivityTimelineProps {
    organizationId: string;
    /** The History list's filter; the counts swap its dates for each chunk's. */
    filter: OrganizationHistoryFilter;
    range: ActivityTimelineRange;
    previewWindow: DensityChartWindow | null;
    today: string;
    chartDensity: ActivityChartDensityControl;
    onWindowChange: (window: DensityChartWindow) => void;
    onWindowPreview: (window: DensityChartWindow | null) => void;
    onFiltersChange: (filters: Partial<OrganizationActivityFilters>) => void;
    /** The client whose joining starts the history, when it isn't the firm's start. */
    historyStartClientName: string | undefined;
}

export function OrganizationActivityTimeline({
    organizationId,
    filter,
    range,
    previewWindow,
    today,
    chartDensity,
    onWindowChange,
    onWindowPreview,
    onFiltersChange,
    historyStartClientName,
}: OrganizationActivityTimelineProps) {
    const peopleById = useOrganizationActivityPeopleById();
    const knownPeople = useMemo(() => [...peopleById.values()], [peopleById]);
    const { people, chunks, queries, onDrawnDaysChange } =
        useActivityTimelineCountsParams({
            filter,
            unit: chartDensity.shownDensity,
            knownPeople,
            today,
        });
    const counts = useOrganizationHistoryDailyCountsQueries({
        organizationId,
        queries,
    });
    const describeHistoryStart = useMemo(
        () => describeOrganizationHistoryStart(historyStartClientName),
        [historyStartClientName],
    );
    const toSeriesFilters = useCallback(
        (key: TimelineSeriesKey): Partial<OrganizationActivityFilters> =>
            toSliceActorPicks({ key, filter }),
        [filter],
    );

    return (
        <ActivityPageTimeline
            counts={counts}
            chunks={chunks}
            onDrawnDaysChange={onDrawnDaysChange}
            people={people}
            picks={filter}
            series={ORGANIZATION_TIMELINE_SERIES}
            closestUnit={chartDensity.closestDensity}
            onShownUnitChange={chartDensity.showDensity}
            range={range}
            previewWindow={previewWindow}
            today={today}
            onWindowChange={onWindowChange}
            onWindowPreview={onWindowPreview}
            toSeriesFilters={toSeriesFilters}
            clearedActorFilters={CLEARED_ORGANIZATION_ACTOR_SELECTION}
            onFiltersChange={onFiltersChange}
            describeHistoryStart={describeHistoryStart}
        />
    );
}
